import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Eye, ShieldAlert, X } from 'lucide-react';
import type { McpPendingActionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Pill, type PillVariant } from '~/components/pill';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatDateTime } from '~/lib/format-date';
import { useAiPendingActionsList, useAiPendingActionMutations } from '~/hooks/use-ai-pending-actions';

/** Server statuses (PendingActionStatus, stringified by the endpoint). */
const STATUS_PILLS: Record<string, PillVariant> = {
	Pending: 'warning',
	Executing: 'info',
	Executed: 'success',
	Rejected: 'neutral',
	Failed: 'danger'
};

const isConflict = (err: unknown) => (err as { response?: { status?: number } })?.response?.status === 409;

/** Pretty-print the stored tool arguments (raw JSON exactly as the model submitted them). */
const prettyArgs = (raw?: string) => {
	if (!raw) return '';
	try {
		return JSON.stringify(JSON.parse(raw), null, 2);
	} catch {
		return raw;
	}
};

/**
 * The AI pending-actions approval queue (docs/ai/16): every risk-gated assistant/MCP tool call the
 * backend queued instead of executing — including ones raised by OTHER admins or in closed chat
 * conversations (the in-chat cards of docs 14/15 only cover the current conversation). Approving
 * EXECUTES the action once; the server enforces the System role and single-fire (409 = already
 * handled). The status/tool vocabulary deliberately mirrors the chat pending-card so operators
 * recognize the same objects at scale.
 */
export function AiPendingActionsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useAiPendingActionsList();
	const { approve, reject } = useAiPendingActionMutations();

	const [view, setView] = useState<'pending' | 'all'>('pending');
	const [detailsRow, setDetailsRow] = useState<McpPendingActionToken | null>(null);
	const [approveRow, setApproveRow] = useState<McpPendingActionToken | null>(null);
	const [rejectRow, setRejectRow] = useState<McpPendingActionToken | null>(null);

	const all = useMemo(() => data ?? [], [data]);
	const pendingCount = useMemo(() => all.filter(a => a.status === 'Pending').length, [all]);
	const rows = view === 'pending' ? all.filter(a => a.status === 'Pending') : all;

	const columns = useMemo<SystemGridColumn<McpPendingActionToken>[]>(
		() => [
			{
				key: 'id',
				header: t('system.aiPendingActions.columns.id'),
				width: '70px',
				align: 'right',
				sortValue: a => a.id ?? 0,
				exportValue: a => a.id ?? '',
				render: a => <span className="text-xs tabular-nums text-foreground-muted">#{a.id}</span>
			},
			{
				key: 'tool',
				header: t('system.aiPendingActions.columns.tool'),
				width: 'minmax(0,1fr)',
				sortValue: a => a.toolName ?? '',
				exportValue: a => a.toolName ?? '',
				render: a => (
					<Badge variant="outline" className="font-mono text-xs">
						{a.toolName}
					</Badge>
				)
			},
			{
				key: 'arguments',
				header: t('system.aiPendingActions.columns.arguments'),
				width: 'minmax(0,1.4fr)',
				exportValue: a => a.arguments ?? '',
				render: a => (
					<span className="block truncate font-mono text-xs text-foreground-muted" title={a.arguments ?? ''}>
						{a.arguments || '—'}
					</span>
				)
			},
			{
				key: 'requestedBy',
				header: t('system.aiPendingActions.columns.requestedBy'),
				width: '180px',
				sortValue: a => a.requestedBy ?? '',
				exportValue: a => a.requestedBy ?? '',
				render: a => <span className="truncate text-sm text-foreground-muted">{a.requestedBy || '—'}</span>
			},
			{
				key: 'estimated',
				header: t('system.aiPendingActions.columns.estimated'),
				width: '110px',
				align: 'center',
				sortValue: a => a.estimatedAffected ?? -1,
				exportValue: a => a.estimatedAffected ?? '',
				// Request-time routing estimate — presented as such, never as a guaranteed count (doc 13).
				render: a =>
					a.estimatedAffected == null ? (
						<span className="text-xs text-foreground-disabled">—</span>
					) : (
						<span className="text-xs tabular-nums text-foreground-muted">≈{a.estimatedAffected}</span>
					)
			},
			{
				key: 'status',
				header: t('system.aiPendingActions.columns.status'),
				width: '120px',
				align: 'center',
				sortValue: a => a.status ?? '',
				exportValue: a => a.status ?? '',
				render: a => (
					<Pill variant={STATUS_PILLS[a.status ?? ''] ?? 'neutral'}>
						{t(`system.aiPendingActions.status.${a.status ?? 'Pending'}`, { defaultValue: a.status ?? '' })}
					</Pill>
				)
			},
			{
				key: 'createdAt',
				header: t('system.aiPendingActions.columns.requestedAt'),
				width: '170px',
				sortValue: a => (a.createdAt ? String(a.createdAt) : ''),
				exportValue: a => formatDateTime(a.createdAt as string | undefined),
				render: a => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(a.createdAt as string | undefined)}</span>
			},
			{
				key: 'approvedBy',
				header: t('system.aiPendingActions.columns.decidedBy'),
				width: '180px',
				defaultVisible: false,
				sortValue: a => a.approvedBy ?? '',
				exportValue: a => a.approvedBy ?? '',
				render: a => <span className="truncate text-sm text-foreground-muted">{a.approvedBy || '—'}</span>
			},
			{
				key: 'approvedAt',
				header: t('system.aiPendingActions.columns.decidedAt'),
				width: '170px',
				defaultVisible: false,
				sortValue: a => (a.approvedAt ? String(a.approvedAt) : ''),
				exportValue: a => formatDateTime(a.approvedAt as string | undefined),
				render: a => (
					<span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(a.approvedAt as string | undefined)}</span>
				)
			},
			{
				key: 'keyId',
				header: t('system.aiPendingActions.columns.keyId'),
				width: '90px',
				align: 'center',
				defaultVisible: false,
				sortValue: a => a.mcpKeyId ?? 0,
				exportValue: a => a.mcpKeyId ?? '',
				render: a => <span className="text-xs tabular-nums text-foreground-muted">{a.mcpKeyId ?? '—'}</span>
			}
		],
		[t]
	);

	const confirmApprove = () => {
		if (approveRow?.id == null) return;
		approve.mutate(approveRow.id, {
			onSuccess: res => {
				if (res.executed) toast.success(t('system.aiPendingActions.approvedToast'));
				else toast.error(res.message || t('system.aiPendingActions.executionFailedToast'));
				setApproveRow(null);
			},
			onError: err => {
				toast.error(isConflict(err) ? t('system.aiPendingActions.conflictToast') : serverMessage(err));
				setApproveRow(null);
			}
		});
	};

	const confirmReject = () => {
		if (rejectRow?.id == null) return;
		reject.mutate(rejectRow.id, {
			onSuccess: () => {
				toast.success(t('system.aiPendingActions.rejectedToast'));
				setRejectRow(null);
			},
			onError: err => {
				toast.error(isConflict(err) ? t('system.aiPendingActions.conflictToast') : serverMessage(err));
				setRejectRow(null);
			}
		});
	};

	return (
		<>
			{/* Uncontrolled + onValueChange — the house Tabs idiom (no controlled-value precedent exists). */}
			<Tabs defaultValue="pending" onValueChange={v => setView(v as 'pending' | 'all')} className="mb-4">
				<TabsList>
					<TabsTrigger value="pending">{t('system.aiPendingActions.tabs.pending', { count: pendingCount })}</TabsTrigger>
					<TabsTrigger value="all">{t('system.aiPendingActions.tabs.all', { count: all.length })}</TabsTrigger>
				</TabsList>
			</Tabs>

			<SystemGrid<McpPendingActionToken>
				gridId="ai-pending-actions"
				title={t('system.aiPendingActions.title')}
				rows={rows}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={a => a.id ?? 0}
				columns={columns}
				searchPredicate={(a, q) => `${a.toolName ?? ''} ${a.requestedBy ?? ''} ${a.arguments ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'id', dir: 'desc' }}
				emptyIcon={<ShieldAlert size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				rowActions={a => (
					<>
						<Button
							variant="ghost"
							size="icon"
							type="button"
							aria-label={t('system.aiPendingActions.details')}
							title={t('system.aiPendingActions.details')}
							onClick={() => setDetailsRow(a)}>
							<Eye size={15} />
						</Button>
						{a.status === 'Pending' && (
							<>
								<Button
									variant="ghost"
									size="icon"
									type="button"
									aria-label={t('system.aiPendingActions.approve')}
									title={t('system.aiPendingActions.approve')}
									disabled={approve.isPending}
									onClick={() => setApproveRow(a)}>
									<Check size={15} className="text-success-fg" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									type="button"
									aria-label={t('system.aiPendingActions.reject')}
									title={t('system.aiPendingActions.reject')}
									disabled={reject.isPending}
									onClick={() => setRejectRow(a)}>
									<X size={15} className="text-danger-fg" />
								</Button>
							</>
						)}
					</>
				)}
				exportFilename="ai-pending-actions"
			/>

			{/* Read-only details — the full stored arguments, pretty-printed, plus the decision trail. */}
			<Dialog open={Boolean(detailsRow)} onOpenChange={open => !open && setDetailsRow(null)}>
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							{t('system.aiPendingActions.detailsTitle', { id: detailsRow?.id })}
							<Badge variant="outline" className="font-mono text-xs">
								{detailsRow?.toolName}
							</Badge>
						</DialogTitle>
						<DialogDescription>
							{t('system.aiPendingActions.detailsMeta', {
								requestedBy: detailsRow?.requestedBy || '—',
								requestedAt: formatDateTime(detailsRow?.createdAt as string | undefined)
							})}
							{detailsRow?.approvedBy &&
								` · ${t('system.aiPendingActions.detailsDecision', {
									decidedBy: detailsRow.approvedBy,
									decidedAt: formatDateTime(detailsRow.approvedAt as string | undefined)
								})}`}
						</DialogDescription>
					</DialogHeader>
					<pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 font-mono text-xs whitespace-pre-wrap text-foreground">
						{prettyArgs(detailsRow?.arguments) || '—'}
					</pre>
					{detailsRow?.status === 'Pending' && (
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setRejectRow(detailsRow);
									setDetailsRow(null);
								}}>
								{t('system.aiPendingActions.reject')}
							</Button>
							<Button
								onClick={() => {
									setApproveRow(detailsRow);
									setDetailsRow(null);
								}}>
								{t('system.aiPendingActions.approve')}
							</Button>
						</DialogFooter>
					)}
				</DialogContent>
			</Dialog>

			{/* Approving EXECUTES the queued action — the confirm says exactly that. */}
			<Dialog open={Boolean(approveRow)} onOpenChange={open => !open && setApproveRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.aiPendingActions.approveTitle', { id: approveRow?.id })}</DialogTitle>
						<DialogDescription>{t('system.aiPendingActions.approveBody', { tool: approveRow?.toolName ?? '' })}</DialogDescription>
					</DialogHeader>
					<pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 font-mono text-xs whitespace-pre-wrap text-foreground">
						{prettyArgs(approveRow?.arguments) || '—'}
					</pre>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setApproveRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmApprove} disabled={approve.isPending}>
							{t('system.aiPendingActions.approveConfirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(rejectRow)} onOpenChange={open => !open && setRejectRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.aiPendingActions.rejectTitle', { id: rejectRow?.id })}</DialogTitle>
						<DialogDescription>{t('system.aiPendingActions.rejectBody', { tool: rejectRow?.toolName ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setRejectRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmReject} disabled={reject.isPending}>
							{t('system.aiPendingActions.reject')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
